import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ConfirmDialogTitle } from "@/components/ui/ConfirmDialog/ConfirmDialogTitle";
import { ConfirmDialogBody } from "@/components/ui/ConfirmDialog/ConfirmDialogBody";
import { ConfirmDialogActions } from "@/components/ui/ConfirmDialog/ConfirmDialogActions";
import { useCategoryDocumentCount } from "@/hooks/use-document-categories";
import type { Id } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

interface CategoryDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  categoryName: string;
  categoryId: Id<"documentCategories"> | null;
  onConfirm: () => Promise<void>;
}

export function CategoryDeleteDialog({
  open,
  onClose,
  categoryName,
  categoryId,
  onConfirm,
}: CategoryDeleteDialogProps) {
  const { t } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const { count: documentCount, isLoading: isCountLoading } =
    useCategoryDocumentCount(categoryId);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const canDelete = documentCount === 0;

  return (
    <ConfirmDialog open={open} onClose={onClose} forced>
      <ConfirmDialogTitle>{t("categories.delete.title")}</ConfirmDialogTitle>
      <ConfirmDialogBody>
        <div className="space-y-4 py-2">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm text-primary">
                {t("categories.delete.confirmMessage", { name: categoryName })}
              </p>
              {isCountLoading ? (
                <p className="text-sm text-secondary">
                  {t("categories.delete.checkingUsage")}
                </p>
              ) : documentCount > 0 ? (
                <p className="text-sm text-amber-600">
                  {t("categories.delete.usedByDocuments", {
                    count: documentCount,
                  })}
                </p>
              ) : (
                <p className="text-sm text-secondary">
                  {t("categories.delete.notUsed")}
                </p>
              )}
            </div>
          </div>
        </div>
      </ConfirmDialogBody>
      <ConfirmDialogActions
        onConfirm={handleConfirm}
        onCancel={onClose}
        confirmLabel={tCommon("deleteDialog.delete")}
        variant="danger"
        confirmDisabled={!canDelete || isCountLoading}
        loading={loading}
      />
    </ConfirmDialog>
  );
}
